import { useEffect, useState } from "react";
import DeskCloseX from "@/components/common/DeskCloseX";
import { Mail, Send, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import { DSelect } from "@/components/common/DeskSelect";

const inp = { background: "#101013", border: "0.5px solid #3a3a42", borderRadius: 7, padding: "7px 10px", color: "#e8e8ec", fontSize: 12.5, width: "100%", outline: "none" };
const lbl = { fontSize: 10, letterSpacing: ".06em", textTransform: "uppercase", color: "#8a8a8f", marginBottom: 4, display: "block" };
const EMAIL_RX = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;

/** Envoi manuel d'un modèle depuis un contexte (réservation / vente / lot / contact) : choix du modèle, relecture, envoi. */
export default function SendEmailDialog({ context, objectId, onClose, onSent }) {
  const [templates, setTemplates] = useState(null);
  const [tplId, setTplId] = useState("");
  const [draft, setDraft] = useState(null);
  const [clientEmail, setClientEmail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/email-templates/manual", { params: { context } }).then(({ data }) => setTemplates(data.items || [])).catch(() => setTemplates([])); }, [context]);
  useEffect(() => {
    if (!tplId) return setDraft(null);
    api.get(`/email-templates/${tplId}/prepare`, { params: { context, object_id: objectId } }).then(({ data }) => { setDraft(data); setClientEmail(""); }).catch((e) => toast.error(e?.response?.data?.detail || "Erreur"));
  }, [tplId, context, objectId]);

  const send = async () => {
    const to = [...(draft.to || [])];
    let save;
    if (draft.client_email_missing) {
      const em = clientEmail.trim().toLowerCase();
      if (!EMAIL_RX.test(em)) return toast.error("Adresse email du client invalide");
      to.push(em); save = em;
    }
    if (!to.length) return toast.error("Aucun destinataire");
    setBusy(true);
    try {
      const { data } = await api.post("/email-templates/send", { template_id: tplId, context, object_id: objectId, subject: draft.subject, body: draft.body, to, save_client_email: save });
      if (data.failed) toast.error(`${data.failed} envoi(s) en échec`); else toast.success(`Email envoyé à ${to.join(", ")}`);
      onSent?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'envoi"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center" style={{ background: "rgba(0,0,0,.65)" }} onClick={onClose} data-testid="send-email-dialog" data-desk-modal="">
      <div className="flex flex-col" style={{ width: 680, maxWidth: "94vw", maxHeight: "92vh", background: "#0e0e10", border: "0.5px solid #2a2a30", borderRadius: 12 }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between" style={{ padding: "14px 18px", borderBottom: "0.5px solid #232328" }}>
          <div className="flex items-center" style={{ gap: 8 }}><Mail size={14} style={{ color: "#2B579A" }} /><span style={{ fontSize: 15, color: "#f2f2f5", fontWeight: 600 }}>Envoyer un email</span></div>
          <DeskCloseX onClick={onClose} testId="send-email-close" />
        </div>
        <div className="overflow-y-auto grid" style={{ padding: 18, gap: 12 }}>
          <div>
            <span style={lbl}>Modèle (manuel, actif)</span>
            {templates && templates.length === 0 ? <div style={{ fontSize: 12, color: "#8a8a8f", fontStyle: "italic" }} data-testid="send-email-none">Aucun modèle manuel actif pour ce contexte — créez-en un dans Paramètres › Modèles d'emails.</div>
              : <DSelect value={tplId} onChange={(e) => setTplId(e.target.value)} style={{ ...inp, appearance: "auto" }} data-testid="send-email-template"><option value="">— Choisir un modèle —</option>{(templates || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</DSelect>}
          </div>
          {draft && (<>
            <div>
              <span style={lbl}>Destinataires</span>
              <div className="flex flex-wrap" style={{ gap: 6 }} data-testid="send-email-to">
                {draft.to.map((e) => <span key={e} style={{ fontSize: 11.5, padding: "3px 8px", borderRadius: 6, background: "#17171b", border: "0.5px solid #2a2a30", color: "#d6d6dc" }}>{e}</span>)}
                {draft.client_email_missing && <span className="inline-flex items-center" style={{ gap: 4, fontSize: 11, color: "#E0A82E" }}><AlertTriangle size={11} /> Le client n'a pas d'adresse email</span>}
              </div>
              {draft.client_email_missing && <div style={{ marginTop: 6 }}><input type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} placeholder="Saisir l'email du client (sera enregistré sur le contact)" style={inp} data-testid="send-email-client-email" /></div>}
            </div>
            <div><span style={lbl}>Objet</span><input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} style={inp} data-testid="send-email-subject" /></div>
            <div><span style={lbl}>Corps — relisez et ajustez avant envoi</span><textarea rows={12} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} style={{ ...inp, resize: "vertical", lineHeight: 1.5 }} data-testid="send-email-body" /></div>
          </>)}
        </div>
        <div className="flex items-center justify-end" style={{ gap: 8, padding: "12px 18px", borderTop: "0.5px solid #232328" }}>
          <button type="button" onClick={onClose} style={{ fontSize: 12, padding: "8px 14px", borderRadius: 7, color: "#c9c9d1" }} data-testid="send-email-cancel">Annuler</button>
          <button type="button" onClick={send} disabled={!draft || busy} className="inline-flex items-center" style={{ gap: 6, fontSize: 12, padding: "8px 16px", borderRadius: 7, background: "#2B579A", color: "#fff", fontWeight: 700, opacity: !draft || busy ? .5 : 1 }} data-testid="send-email-submit">{busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Envoyer</button>
        </div>
      </div>
    </div>
  );
}
